import { ProfessionTypeEnum } from '~/models';
import { TNullable } from '~/types/Nullable';

/**
 * Its two consumers deliberately match against it differently, and unifying them
 * would be a regression either way: `useProfessionOptions` filters the canonical
 * `ProfessionalProfessions` list, where an exact `includes` is exactly right,
 * while `isExcludedProfession` below guards free-form parser output and so has to
 * normalize before comparing.
 */
export const EXCLUDED_PROFESSIONS = [ProfessionTypeEnum.DN];

const normalizeProfession = (profession: string) => profession.trim().toUpperCase();

/**
 * Whether a profession code is one the permanent-jobs flow does not support, so
 * every layer (dropdowns, parsed-posting mapping, form validation) agrees on
 * what "excluded" means instead of hand-rolling its own match.
 *
 * Matching is normalized — trimmed and case-insensitive — because not every
 * value reaching this check comes from a dropdown: the `POST /postings/parse`
 * LLM returns free-form codes, so `'dn'` and `' DN '` name the same excluded
 * profession as `'DN'`. The check has to fail closed. Its siblings in that
 * pipeline degrade to `null` when they cannot recognise a value, which merely
 * asks the office to pick manually; a miss here would instead publish the very
 * profession we are refusing.
 *
 * Only the excluded codes match. Other values the flow cannot use — an
 * unrecognised code such as `'OM'`, or a prose profession name — are left for
 * form validation to reject.
 */
export const isExcludedProfession = (profession: TNullable<string>): boolean =>
  typeof profession === 'string' &&
  EXCLUDED_PROFESSIONS.some(
    (excluded) => normalizeProfession(excluded) === normalizeProfession(profession),
  );

/** The profession-carrying fields of a posting — as much of an entity as this rule needs. */
interface IProfessionCarrier {
  readonly professionType?: TNullable<string>;
  readonly subProfession?: TNullable<string>;
}

/**
 * The profession a posting effectively carries.
 *
 * Sub-profession wins, the same precedence `JobDetailsFormMapper.fromEntity`
 * uses to seed `professionType` on the form. Anything deciding from a posting's
 * profession has to agree with the value the form is actually holding, or the
 * two drift: a posting whose `professionType` is supported can still be an
 * excluded profession through `subProfession`, and reading only
 * `professionType` would call it supported.
 */
export const getEffectiveProfession = (entity?: TNullable<IProfessionCarrier>): TNullable<string> =>
  entity?.subProfession || entity?.professionType;
